import React, { useState, useEffect } from 'react';
import { PlusOutlined, EyeOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, PrinterOutlined, CalendarOutlined, FileTextOutlined } from '@ant-design/icons';
import { Popconfirm, Divider, InputNumber, DatePicker, Row, Col, Typography, Space, message, Select, Input, Form, Modal, Button, Table, Card, Layout, Grid, Empty, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { generateJournalEntryPDF } from '../../services/journalEntryPdfService';
import './JournalEntriesPage.css';

const { Title, Text } = Typography;
const { Option } = Select;

const JournalEntriesPage = () => {
    const [entries, setEntries] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    useEffect(() => {
        fetchEntries();
        fetchGlAccounts();
        fetchCompanyInfo();
    }, []);

    const fetchCompanyInfo = async () => {
        try {
            const res = await api.get('/api/settings/company');
            if (res.data.success) {
                setCompanyInfo(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch company info');
        }
    };

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/accounting/journal-entries');
            if (res.data.success) {
                setEntries(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลสมุดรายวันได้');
        } finally {
            setLoading(false);
        }
    };

    const fetchGlAccounts = async () => {
        try {
            const res = await api.get('/api/accounting/accounts');
            if (res.data.success) {
                setGlAccounts(res.data.data);
            }
        } catch (error) { }
    };

    const handleView = async (id) => {
        try {
            const res = await api.get(`/api/accounting/journal-entries/${id}`);
            if (res.data.success) {
                setSelectedEntry(res.data.data);
                setIsViewModalVisible(true);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงรายละเอียดได้');
        }
    };

    useEffect(() => {
        const id=Number(new URLSearchParams(window.location.search).get('entry_id'));
        if(id>0) handleView(id);
    },[]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePrint = async (id) => {
        try {
            const res = await api.get(`/api/accounting/journal-entries/${id}`);
            if (res.data.success) {
                const dataUrl = await generateJournalEntryPDF(res.data.data, companyInfo);
                setPdfUrl(dataUrl);
                setIsPreviewModalVisible(true);
            }
        } catch (error) {
            message.error('ไม่สามารถสร้างเอกสารได้');
        }
    };

    const handlePrintAll = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/accounting/journal-entries/all-details');
            if (res.data.success) {
                if (res.data.data.length === 0) {
                    return message.warning('ไม่มีข้อมูลให้พิมพ์');
                }
                const dataUrl = await generateJournalEntryPDF(res.data.data, companyInfo);
                setPdfUrl(dataUrl);
                setIsPreviewModalVisible(true);
            }
        } catch (error) {
            message.error('ไม่สามารถสร้างเอกสารรวมได้');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        const { entry_date, description, reference_no, items } = values;

        // Basic validation
        const totalDebit = items.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0);
        const totalCredit = items.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);

        if (Math.abs(totalDebit - totalCredit) > 0.01) {
            return message.error('ยอดเดบิตและเครดิตไม่สมดุลกัน');
        }

        try {
            if (editingItem) {
                await api.put(`/api/accounting/journal-entries/${editingItem.id}`, {
                    entry_date: entry_date.format('YYYY-MM-DD'),
                    description,
                    reference_no,
                    items
                });
                message.success('แก้ไขรายการรายวันเรียบร้อย');
            } else {
                await api.post('/api/accounting/journal-entries', {
                    entry_date: entry_date.format('YYYY-MM-DD'),
                    description,
                    reference_no,
                    items
                });
                message.success('บันทึกรายการรายวันเรียบร้อย');
            }
            setIsModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchEntries();
        } catch (error) {
            message.error('ไม่สามารถบันทึกรายการได้');
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await api.get(`/api/accounting/journal-entries/${id}`);
            if (res.data.success) {
                const entry = res.data.data;
                setEditingItem(entry);
                form.setFieldsValue({
                    entry_date: dayjs(entry.entry_date),
                    description: entry.description,
                    reference_no: entry.reference_no,
                    items: entry.items.map(item => ({
                        account_id: item.account_id,
                        debit: parseFloat(item.debit) || 0,
                        credit: parseFloat(item.credit) || 0,
                        description: item.description
                    }))
                });
                setIsModalVisible(true);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงรายละเอียดได้');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/api/accounting/journal-entries/${id}`);
            message.success('ลบรายการเรียบร้อย');
            fetchEntries();
        } catch (error) {
            message.error('ไม่สามารถลบรายการได้');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        form.setFieldsValue({ entry_date: dayjs(), items: [{}, {}] });
        setIsModalVisible(true);
    };

    const formatAmount = (value) => Number(value || 0).toLocaleString('th-TH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const columns = [
        { title: 'เลขที่ JV', dataIndex: 'jv_number', key: 'jv_number' },
        { title: 'วันที่', dataIndex: 'entry_date', key: 'entry_date', render: (date) => dayjs(date).format('DD/MM/YYYY') },
        { title: 'รายละเอียด', dataIndex: 'description', key: 'description' },
        { title: 'อ้างอิง', dataIndex: 'reference_no', key: 'reference_no' },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record.id)}>ดู</Button>
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record.id)}>แก้ไข</Button>
                    <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDelete(record.id)} okText="ใช่" cancelText="ไม่">
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                    <Button icon={<PlusOutlined />} size="small" onClick={() => handlePrint(record.id)}>พิมพ์</Button>
                </Space>
            )
        }
    ];

    return (
        <Layout className="journal-page">
            <Space direction="vertical" style={{ width: '100%' }} size={isMobile ? 14 : 'large'}>
                <div className="journal-page__header">
                    <div>
                        <Title level={isMobile ? 3 : 2} className="journal-page__title">สมุดรายวันทั่วไป</Title>
                        <Text type="secondary">General Journal · บันทึกและตรวจสอบรายการบัญชี</Text>
                    </div>
                    <Space wrap className="journal-page__actions">
                        <Button icon={<ReloadOutlined />} onClick={fetchEntries}>รีเฟรช</Button>
                        <Button icon={<PrinterOutlined />} onClick={handlePrintAll}>{isMobile ? 'พิมพ์' : 'พิมพ์ทั้งหมด'}</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>ลงรายการใหม่</Button>
                    </Space>
                </div>

                {isMobile ? (
                    <div className="journal-mobile-list">
                        <Spin spinning={loading}>
                            {entries.length === 0 ? (
                                <Card><Empty description="ยังไม่มีรายการสมุดรายวัน" /></Card>
                            ) : entries.map(entry => {
                                const difference = Math.abs(Number(entry.total_debit || 0) - Number(entry.total_credit || 0));
                                return (
                                    <Card key={entry.id} className="journal-entry-card" bordered={false}>
                                        <div className="journal-entry-card__top">
                                            <div>
                                                <Text strong className="journal-entry-card__number">{entry.jv_number}</Text>
                                                <div className="journal-entry-card__date"><CalendarOutlined /> {dayjs(entry.entry_date).format('DD/MM/YYYY')}</div>
                                            </div>
                                            <Tag color={difference < 0.01 ? 'success' : 'error'}>{difference < 0.01 ? 'สมดุล' : 'ไม่สมดุล'}</Tag>
                                        </div>
                                        <div className="journal-entry-card__description">
                                            <FileTextOutlined /> <span>{entry.description || 'ไม่มีคำอธิบาย'}</span>
                                        </div>
                                        {entry.reference_no && <div className="journal-entry-card__reference">อ้างอิง: {entry.reference_no}</div>}
                                        <div className="journal-entry-card__amounts">
                                            <div><span>เดบิต</span><strong>{formatAmount(entry.total_debit)} ฿</strong></div>
                                            <div><span>เครดิต</span><strong>{formatAmount(entry.total_credit)} ฿</strong></div>
                                        </div>
                                        <div className="journal-entry-card__footer">
                                            <Button icon={<EyeOutlined />} onClick={() => handleView(entry.id)}>ดู</Button>
                                            <Button icon={<EditOutlined />} onClick={() => handleEdit(entry.id)}>แก้ไข</Button>
                                            <Button icon={<PrinterOutlined />} onClick={() => handlePrint(entry.id)} aria-label="พิมพ์" />
                                            <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDelete(entry.id)} okText="ใช่" cancelText="ไม่">
                                                <Button icon={<DeleteOutlined />} danger aria-label="ลบ" />
                                            </Popconfirm>
                                        </div>
                                    </Card>
                                );
                            })}
                        </Spin>
                    </div>
                ) : (
                    <Card className="journal-table-card">
                        <Table columns={columns} dataSource={entries} rowKey="id" loading={loading} />
                    </Card>
                )}
            </Space>

            {/* Create JV Modal */}
            <Modal
                title={editingItem ? `แก้ไขรายการรายวัน: ${editingItem.jv_number}` : "ลงรายการรายวันทั่วไป (Journal Voucher)"}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingItem(null);
                }}
                onOk={() => form.submit()}
                width={isMobile ? '100%' : 800}
                className="journal-form-modal"
                styles={isMobile ? { body: { padding: 16 } } : undefined}
                okText="บันทึกรายการ"
                cancelText="ยกเลิก"
                centered={!isMobile}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : undefined}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="entry_date" label="วันที่" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item name="reference_no" label="เลขที่อ้างอิง">
                                <Input placeholder="เช่น เลขที่ใบเสร็จ, เลขที่โอน" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="description" label="คำอธิบายรายการ" rules={[{ required: true }]}>
                        <Input.TextArea rows={2} placeholder="ระบุรายละเอียดการลงบัญชี" />
                    </Form.Item>

                    <Divider orientation="left">รายการบัญชี (Items)</Divider>

                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={[8, 8]} key={key} align="middle" className="journal-line-item">
                                        {isMobile && <Col span={24} className="journal-line-item__heading"><Text strong>รายการที่ {name + 1}</Text><Button type="text" danger onClick={() => remove(name)} icon={<DeleteOutlined />}>ลบ</Button></Col>}
                                        <Col xs={24} md={10}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'account_id']}
                                                rules={[{ required: true, message: 'เลือกบัญชี' }]}
                                                style={{ marginBottom: 0 }}
                                                label={isMobile ? 'ผังบัญชี' : undefined}
                                            >
                                                <Select showSearch optionFilterProp="children" placeholder="เลือกบัญชี">
                                                    {glAccounts.map(acc => (
                                                        <Option key={acc.id} value={acc.id}>
                                                            {(acc.CODE || acc.code)} - {(acc.NAME || acc.name)}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'debit']}
                                                style={{ marginBottom: 0 }}
                                                label={isMobile ? 'เดบิต' : undefined}
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    placeholder="เดบิต"
                                                    min={0}
                                                    precision={2}
                                                    onChange={() => {
                                                        // Clear credit if debit is entered
                                                        const items = form.getFieldValue('items');
                                                        if (items[name].debit > 0) {
                                                            items[name].credit = 0;
                                                            form.setFieldsValue({ items });
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'credit']}
                                                style={{ marginBottom: 0 }}
                                                label={isMobile ? 'เครดิต' : undefined}
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    placeholder="เครดิต"
                                                    min={0}
                                                    precision={2}
                                                    onChange={() => {
                                                        // Clear debit if credit is entered
                                                        const items = form.getFieldValue('items');
                                                        if (items[name].credit > 0) {
                                                            items[name].debit = 0;
                                                            form.setFieldsValue({ items });
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        {!isMobile && <Col span={2}>
                                            <Button type="text" danger onClick={() => remove(name)} icon={<DeleteOutlined />} />
                                        </Col>}
                                    </Row>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    เพิ่มรายการ
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>

            {/* View JV Modal */}
            <Modal
                title={`รายละเอียดรายการ: ${selectedEntry?.jv_number}`}
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                width={isMobile ? '100%' : 800}
                className="journal-view-modal"
                centered={!isMobile}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : undefined}
                footer={null}
            >
                {selectedEntry && (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Row gutter={[8, 8]}>
                            <Col xs={24} md={12}><Text strong>วันที่:</Text> {dayjs(selectedEntry.entry_date).format('DD/MM/YYYY')}</Col>
                            <Col xs={24} md={12}><Text strong>อ้างอิง:</Text> {selectedEntry.reference_no || '-'}</Col>
                        </Row>
                        <div><Text strong>รายละเอียด:</Text> {selectedEntry.description}</div>

                        {isMobile ? (
                            <div className="journal-detail-list">
                                {selectedEntry.items.map((item, index) => (
                                    <div className="journal-detail-item" key={item.id || index}>
                                        <div><Text strong>{item.account_code || item.ACCOUNT_CODE}</Text> · {item.account_name || item.ACCOUNT_NAME}</div>
                                        <div className="journal-detail-item__amounts">
                                            <span>เดบิต <strong>{Number(item.debit || 0) > 0 ? formatAmount(item.debit) : '-'}</strong></span>
                                            <span>เครดิต <strong>{Number(item.credit || 0) > 0 ? formatAmount(item.credit) : '-'}</strong></span>
                                        </div>
                                    </div>
                                ))}
                                <div className="journal-detail-total">
                                    <span>รวมเดบิต <strong>{formatAmount(selectedEntry.items.reduce((sum, item) => sum + Number(item.debit || 0), 0))}</strong></span>
                                    <span>รวมเครดิต <strong>{formatAmount(selectedEntry.items.reduce((sum, item) => sum + Number(item.credit || 0), 0))}</strong></span>
                                </div>
                            </div>
                        ) : <Table
                            dataSource={selectedEntry.items}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                {
                                    title: 'รหัสบัญชี',
                                    key: 'account_code',
                                    render: (_, record) => record.account_code || record.ACCOUNT_CODE
                                },
                                {
                                    title: 'ชื่อบัญชี',
                                    key: 'account_name',
                                    render: (_, record) => record.account_name || record.ACCOUNT_NAME
                                },
                                {
                                    title: 'เดบิต',
                                    dataIndex: 'debit',
                                    key: 'debit',
                                    align: 'right',
                                    render: (val) => val > 0 ? parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'
                                },
                                {
                                    title: 'เครดิต',
                                    dataIndex: 'credit',
                                    key: 'credit',
                                    align: 'right',
                                    render: (val) => val > 0 ? parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'
                                }
                            ]}
                            summary={pageData => {
                                let totalDebit = 0;
                                let totalCredit = 0;
                                pageData.forEach(({ debit, credit }) => {
                                    totalDebit += parseFloat(debit || 0);
                                    totalCredit += parseFloat(credit || 0);
                                });
                                return (
                                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                        <Table.Summary.Cell index={0} colSpan={2} align="right"><Text strong>รวม</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right"><Text strong>{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} align="right"><Text strong>{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />}
                    </Space>
                )}
            </Modal>
            {/* PDF Preview Modal */}
            <Modal
                title="ตัวอย่างก่อนพิมพ์"
                open={isPreviewModalVisible}
                onCancel={() => setIsPreviewModalVisible(false)}
                width={isMobile ? '100%' : '80%'}
                className="journal-pdf-modal"
                centered={!isMobile}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : undefined}
                footer={[
                    <Button key="close" onClick={() => setIsPreviewModalVisible(false)}>ปิด</Button>,
                    <Button key="print" type="primary" onClick={() => {
                        const printWindow = window.open(pdfUrl);
                        if (printWindow) printWindow.print();
                    }}>พิมพ์เอกสาร</Button>
                ]}
            >
                {pdfUrl && (
                    <iframe
                        src={pdfUrl}
                        style={{ width: '100%', height: isMobile ? '72vh' : '70vh', border: 'none' }}
                        title="PDF Preview"
                    />
                )}
            </Modal>
        </Layout>
    );
};

export default JournalEntriesPage;
